/**
 * PendingTransferCard Component
 * Displays a pending transfer summary with value grid,
 * status badge, and approval button.
 */

import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ArrowRightLeft } from 'lucide-react';
import type { TransferPending } from '@/types/collection.types';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

const formatDate = (dateStr: string) =>
  new Date(dateStr + 'T12:00:00').toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

interface PendingTransferCardProps {
  transfer: TransferPending | null;
  isLoading: boolean;
  onApprove: (transferId: string) => void;
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'PENDING' ? 'warning' : 'success';
  const label = status === 'PENDING' ? 'Pendiente' : 'Completada';
  return <Badge variant={variant}>{label}</Badge>;
}

export function PendingTransferCard({ transfer, isLoading, onApprove }: PendingTransferCardProps) {
  if (isLoading) {
    return (
      <Card title="Transferencia Pendiente">
        <div className="space-y-4">
          <Skeleton width="60%" height={20} variant="text" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} width="100%" height={60} variant="rectangular" />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!transfer) {
    return (
      <Card title="Transferencia Pendiente">
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <ArrowRightLeft size={32} className="text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No hay transferencias pendientes.
          </p>
        </div>
      </Card>
    );
  }

  const gridItems = [
    { label: 'Principal', value: formatCurrency(transfer.total_principal_mxn) },
    { label: 'Interes UNE-Credito', value: formatCurrency(transfer.total_partner_interest_mxn) },
    { label: 'Interes Fiado', value: formatCurrency(transfer.total_fiado_interest_mxn) },
    { label: 'Total a Transferir', value: formatCurrency(transfer.total_amount_mxn) },
    { label: 'Registros', value: String(transfer.records_count) },
  ];

  return (
    <Card title="Transferencia Pendiente">
      <div className="space-y-4">
        {/* Period + Status */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Periodo: {formatDate(transfer.period_start)} - {formatDate(transfer.period_end)}
          </p>
          <StatusBadge status={transfer.status} />
        </div>

        {/* Value grid */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {(gridItems ?? []).map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-border bg-muted/30 p-3"
            >
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Approve button (only for pending) */}
        {transfer.status === 'PENDING' && (
          <div className="flex justify-end pt-2">
            <Button
              variant="primary"
              leftIcon={<ArrowRightLeft size={16} />}
              onClick={() => onApprove(transfer.transfer_id)}
            >
              Aprobar y Transferir
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
